import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';
import 'src/screen/Protected/DeviceScreen/TestingReport/TestingReport.scss';
import { Box, Tabs, Tab } from '@mui/material';
import { Map, List, fromJS } from 'immutable';
import Button from 'src/component/UI/Button';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import { getDeviceById } from 'src/module/device/selector';
import { fetchDevice } from 'src/module/device/action';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, fetchCheckSheetTasks, saveCheckSheet, updateCheckSheet } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { resolveEffectiveDeviceId } from './utils';
import { useCommissioningDeviceLink } from './useCommissioningDeviceLink';

// Deep-merge edits over the base form, replacing Lists rather than concatenating them the way
// mergeDeep would. A List here is always a dynamic table's rows, edited as a unit.
function mergeEdits (base, edits) {
  return base.mergeWith(function merger (prev, next) {
    if (List.isList(prev) || List.isList(next)) return next;
    if (Map.isMap(prev) && Map.isMap(next)) return prev.mergeWith(merger, next);
    return next;
  }, edits);
}

/**
 * Shared shell for the field report modals, holding the scaffolding every report repeats:
 * task fetching, form state, device link/prefill, tab navigation, submit, modal chrome.
 * Each report supplies only what differs — its type, task types, tabs, and empty form.
 *
 * `renderTab(tabValue, ctx)` receives { allResults, extras, change, readOnly, statusError,
 * linkedDeviceTag, handleUnlink, handleDeviceSelect } and returns that tab's body.
 */
export default function FieldReportModal (props) {
  const { open, reportId, deviceId } = props;
  if (!open) return null;
  // Keyed so a new report/device remounts the body; the modal stays mounted across sessions
  // and would otherwise inherit the previous one's edits and tab.
  return <FieldReportModalBody key={`${reportId || 'new'}-${deviceId || 'unlinked'}`} {...props} />;
}

function FieldReportModalBody (props) {
  const {
    reportType, taskTypes, tabs, buildEmptyResults, renderTab,
    title, prefillFields, deviceId, reportId, open, handleClose,
    requiresStatus = true,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState('1');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // The content region is the scroll container and survives a tab change, so without this a
  // new tab opens at the previous tab's scroll offset. Covers every route to a new tab:
  // the tab bar, Next/Previous, and the submit-validation jump.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    // scrollTop rather than scrollTo: jsdom implements the property but not the method.
    content.scrollTop = 0;
    // A check sheet that fills the tab scrolls its own table rather than the content region,
    // so reset that too (reports with one check sheet per tab).
    content.querySelectorAll('.check-sheet-table-container')
      .forEach((el) => { (el as HTMLElement).scrollTop = 0; });
  }, [tabValue]);

  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading(reportType));
  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const abilities: any = useSelector(getAbilities);

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const readOnly = Boolean(reportId) && !abilities.includes('edit-check-sheet-report');
  const lastTab = tabs[tabs.length - 1].value;

  // Tasks for every check sheet type this report renders (controller has three, most have one).
  const tasksByType = useSelector((state: any) =>
    Map(taskTypes.map(type => [type, state.checkSheet.getIn(['checkSheets', 'tasks', type])])));

  // The form is derived from redux, not stored twice: an existing report is its saved record,
  // a new one is an empty shape built from the task definitions. `edits` holds only what the
  // user has changed, so there is no effect copying redux into state and no stale copy to
  // resync. Merging edits last means a field the user touched always wins.
  const [edits, setEdits] = useState(Map({}));

  const savedReport = reportId ? report : null;
  const allResults = useMemo(() => {
    const base = savedReport?.get('checkSheet')
      ? Map(savedReport)
      : buildEmptyResults(tasksByType, device);
    return mergeEdits(base, edits);
  }, [savedReport, tasksByType, device, buildEmptyResults, edits]);

  useEffect(() => {
    taskTypes.forEach(type => dispatch(fetchCheckSheetTasks(type)));
  }, []);

  // Opened from the reports list the device may never have been visited, so its record isn't
  // in the store. Fetch it so the prefill below can populate the info fields.
  useEffect(() => {
    if (open && deviceId) dispatch(fetchDevice(deviceId));
  }, [open, deviceId, dispatch]);

  useEffect(() => {
    if (open && reportId) dispatch(fetchCheckSheet(reportId, taskTypes[0]));
  }, [open, reportId, dispatch]);

  const linkedDeviceId = allResults.getIn(['info', 'deviceId']) as string | null;
  const linkedDevice = useSelector(getDeviceById(linkedDeviceId || ''));

  // Linking a device fills the fields it knows about. Written as edits, and skipped where the
  // form already has a value, so user input is never clobbered.
  useEffect(() => {
    if (!prefillFields || !linkedDevice || linkedDevice.isEmpty()) return;
    setEdits((prev) => {
      let next = prev;
      prefillFields.forEach(({ path, deviceKey }) => {
        const fullPath = ['extras', ...path];
        if (!allResults.getIn(fullPath)) {
          const val = linkedDevice.get(deviceKey);
          if (val) next = next.setIn(fullPath, val);
        }
      });
      return next;
    });
  }, [linkedDevice, prefillFields]);

  const { handleUnlinkDevice, handleDeviceSelect } = useCommissioningDeviceLink(
    setEdits,
    useCallback((id: string) => dispatch(fetchDevice(id)), [dispatch]),
  );

  const change = useCallback((path, value) => {
    setEdits(prev => prev.setIn(path, value));
  }, []);

  const titleTag = useMemo(
    () => title?.tag ?? device?.get('tag'),
    [title, device]
  );

  if (reportId && !report.get('checkSheet')) return null;

  // Reports without a sign-off status field (HMS) can't satisfy this, so don't gate them on it.
  const statusError = requiresStatus && submitAttempted && !allResults.getIn(['signOff', 'commissioningStatus']);
  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const submit = () => {
    if (requiresStatus && !allResults.getIn(['signOff', 'commissioningStatus'])) {
      setSubmitAttempted(true);
      setTabValue(lastTab);
      return;
    }
    const values: any = allResults.toJS();
    // Task results are stored per check sheet type; the API expects one flat list.
    const checkSheet = taskTypes.flatMap(type => values[type] || []);
    const effectiveDeviceId = resolveEffectiveDeviceId(allResults, deviceId);
    let payload = allResults
      .setIn(['info', 'type'], reportType)
      .setIn(['info', 'deviceId'], effectiveDeviceId)
      .set('checkSheet', fromJS(checkSheet))
      .set('signOff', allResults.get('signOff'));
    if (reportId) {
      payload = payload.set('id', reportId);
      dispatch(updateCheckSheet(payload, effectiveDeviceId, reportType));
    } else {
      dispatch(saveCheckSheet(reportType, payload, effectiveDeviceId));
    }
  };

  const tabContext = {
    allResults,
    extras: allResults?.get('extras'),
    change,
    readOnly,
    statusError,
    linkedDeviceTag: device?.get('tag'),
    handleUnlinkDevice,
    handleDeviceSelect,
  };

  return (
    <Modal open={open} onClose={handleClose} className='field-report-modal'>
      <div className={`modal testing-report-modal commissioning-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile && (
          <h2>{title.label} {titleTag && <b>({titleTag})</b>}</h2>
        )}
        {isLoadingTasks ? (
          <div className='field-report-modal-spinner'>
            <LogoSpinner size={100} />
          </div>
        ) : (
          <>
            <Box className='field-report-tab-bar'>
              <Tabs
                value={tabValue}
                onChange={(_e, val) => setTabValue(val)}
                textColor='primary'
                indicatorColor='primary'
                aria-label={`${reportType}-tabs`}
                variant='scrollable'
                scrollButtons='auto'
              >
                {tabs.map(tab => (
                  <Tab key={tab.value} value={tab.value} label={<span className='tab-label-size'>{tab.label}</span>} />
                ))}
              </Tabs>
            </Box>

            <div className='commissioning-modal-content' ref={contentRef}>
              {renderTab(tabValue, tabContext)}
            </div>

            <div className='button-bar stay-at-bottom'>
              <Button onClick={handleClose}>{t('common.cancel')}</Button>
              {tabValue !== '1' && <Button onClick={previous}>{t('common.previous')}</Button>}
              {tabValue === lastTab
                ? <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('common.save') : t('common.submit')}</Button>
                : <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>
              }
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

FieldReportModalBody.propTypes = {
  reportType: PropTypes.string.isRequired,
  // Check sheet types whose tasks this report renders; results are flattened in this order.
  taskTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  // (tasksByType, device) => Immutable Map of the empty form.
  buildEmptyResults: PropTypes.func.isRequired,
  // (tabValue, ctx) => the body for that tab.
  renderTab: PropTypes.func.isRequired,
  title: PropTypes.shape({ label: PropTypes.string, tag: PropTypes.string }).isRequired,
  // [{ path: ['projectInfo','unitArea'], deviceKey: 'unit' }] — filled only when empty.
  prefillFields: PropTypes.arrayOf(PropTypes.shape({
    path: PropTypes.arrayOf(PropTypes.string).isRequired,
    deviceKey: PropTypes.string.isRequired,
  })),
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
  // False for reports with no sign-off status field, so submit isn't gated on it.
  requiresStatus: PropTypes.bool,
};

FieldReportModal.propTypes = FieldReportModalBody.propTypes;
